import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { UserDetailResponse } from '@edtech-ui/core/models/Classroom/user-detail';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { SharedBreadcrumbComponent } from '../shared-breadcrumb/shared-breadcrumb.component';
import { Router, RouterModule } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { IstDatePipe } from '@edtech-ui/core/models/Classroom/ist-date.pipe';
import { NotificationTypeEnum } from '@edtech-ui/constants/notification.enum';
import { DeleteDialogBoxComponent } from '@edtech-ui/dashboard/teacher/classroom/delete-dialog-box/delete-dialog-box.component';
import { MarkNotificationPayload } from '@edtech-ui/core/models/notifications/marked-notification-response';
import { NotificationService } from '@edtech-ui/core/services/notification-managament-services/notification.service';
import { NotificationItem } from '@edtech-ui/core/models/notifications/get-notification-response';
import { MatDialog } from '@angular/material/dialog';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { Roles } from '@edtech-ui/constants/roles.enum';
import { FireBaseServiceService } from '@edtech-ui/core/services/firebaseService/fire-base-service.service';

@Component({
	selector: 'app-top-navbar',
	standalone: true,
	imports: [MatIconModule, MatToolbarModule, CommonModule, SharedBreadcrumbComponent, MatTooltipModule,
		MatProgressSpinnerModule, IstDatePipe, RouterModule, HasPermissionDirective
	],
	templateUrl: './top-navbar.component.html',
	styleUrl: './top-navbar.component.scss'
})
export class TopNavbarComponent implements OnInit, OnDestroy {

	userDetail: UserDetailResponse['Data'] | null = null;
	subscription = new Subscription();
	userId = '';
	isopenPaymentHistory = false;
	isProfileMenuOpen = false;
	isNotificationOpen = false;
	notificationTypeEnum = NotificationTypeEnum;
	isLoading = false;
	studentNotifications: NotificationItem[] = [];
	totalNotificationCount: number = 0;
	showNotification = false;
	PermissionForStudents = PermissionForStudents;
	isMentor = false;
	@Output() triggerSidenav = new EventEmitter<void>();

	constructor(
		private _authService: AuthServiceService,
		private _toastr: ToastrService,
		private router: Router,
		private _notificationService: NotificationService,
		private dialog: MatDialog,
		private elementRef: ElementRef,
		private _firebaseService: FireBaseServiceService,
	) {
		this.subscription.add(this._notificationService.reload$.subscribe(() => {
			if (this._authService.hasPermission(this.PermissionForStudents.NOTIFICATION_VIEW)) {
				this.getNotifications();
			}
		}));
	}

	ngOnInit(): void {
		const storedId = localStorage.getItem('user_id');
		if (storedId) {
			this.userId = storedId;
			this.getUserInfo(this.userId);
		}
		this.getNotifications();
		this.isMentor = Number(localStorage.getItem('role_id')) === Roles.MENTOR;

		// Refresh notifications when a push arrives in the foreground
		this.subscription.add(
			this._firebaseService.newPushNotification$.subscribe(() => {
				if (this._authService.hasPermission(this.PermissionForStudents.NOTIFICATION_VIEW)) {
					this.getNotifications();
				}
			})
		);

		this.subscription.add(
			this._authService.currentUserEmail.subscribe(email => {
				if (email && this.userDetail) {
					this.userDetail.phone_email = email;
				}
			})
		);

		this.subscription.add(
			this._authService.userStore$.subscribe(userData => {
				if (userData && Object.keys(userData).length > 0) {
					// Direct assignment ensures omitted keys (like empty last_name) don't retain previous values
					this.userDetail = userData as any;
				}
			})
		);
	}

	getUserInfo(id: string) {
		this.subscription.add(
			this._authService.getUserDetails(id).subscribe({
				next: (response: UserDetailResponse) => {
					if (response.Success) {
						this.userDetail = response.Data;
					}
				},
				error: () => this._toastr.error('Something went wrong while fecthing the user Details')
			})
		);
	}

	logout() {
		this._authService.logout(this.router).subscribe({
			next: (response) => {
				if (response.Success) {
					this._toastr.success('Logged out successfully.');
				} else {
					this._toastr.error(response.Message || 'Logout failed');
				}
			},
			error: () => {
				this._toastr.error('An error occurred during logout');
			}
		});
	}


	toggleProfileMenu() {
		this.isProfileMenuOpen = !this.isProfileMenuOpen;
		if (this.isProfileMenuOpen) {
			this.showNotification = false;
		}
	}

	closeProfileMenu() {
		this.isProfileMenuOpen = false;
	}


	openSidenav() {
		this.triggerSidenav.emit();
	}

	openNotification() {
		this.isNotificationOpen = true;
	}

	closeNotification() {
		this.isNotificationOpen = false;
	}

	goToProfile() {
		this.closeProfileMenu();
		const route = this.isMentor ? '/mentor-panel/profile' : '/student-dashboard/profile';
		this.router.navigate([route]);
	}

	getNotifications(): void {
		if (!this._authService.hasPermission(this.PermissionForStudents.NOTIFICATION_VIEW)) {
			return;
		}
		this.isLoading = true;
		this.subscription.add(
			this._notificationService.getNotificationsForStudent().pipe(
				tap(response => {
					if (response.Data && response.Success) {
						this.studentNotifications = response.Data;
						const unreadNotifications = response.Data.filter((n: any) => !n.is_read);
						this.totalNotificationCount = unreadNotifications.length;
					}
				}),
				catchError(() => {
					return of(null);
				}),
				finalize(() => { this.isLoading = false })
			).subscribe()
		);
	}

	putNotifications(notificationIds: string[]) {

		const unreadNotification = this.studentNotifications.filter(n => notificationIds.includes(n.id) && !n.is_read);
		if (!unreadNotification.length) return;

		const payload: MarkNotificationPayload = { notification_ids: unreadNotification.map(n => n.id) }

		this._notificationService.putMarkedNotifications(payload).pipe(
			tap(response => {
				if (response.Success) {
					unreadNotification.forEach(n => n.is_read = true);
					// Decrement badge count by the number of newly-read notifications
					this.totalNotificationCount = Math.max(0,
						this.totalNotificationCount - unreadNotification.length
					);
				}
			}),
			catchError(err => of(null))
		).subscribe();
	}

	deleteNotification(notificationId: string) {
		this.subscription.add(
			this._notificationService.deleteNotification(notificationId).subscribe({
				next: () => {
					this._toastr.success('Notification deleted successfully');
					this.getNotifications();
				},
				error: (err) => {
					this._toastr.error('Failed to delete notification');
				}
			})
		);
	}


	openDeleteNotificationBox(notificationId: string) {
		this.dialog.open(DeleteDialogBoxComponent, {
			width: '360px',
			data: { notificationId }
		}).afterClosed().subscribe(result => {
			if (result) {
				this.deleteNotification(notificationId);
			}
		});
	}

	openNotifications() {
		this.isNotificationOpen = !this.isNotificationOpen;
	}

	markAllNotificationsAsRead() {
		const unreadIds = this.studentNotifications.filter(n => !n.is_read).map(n => n.id);
		if (!unreadIds.length) return;
		// Badge count resets inside putNotifications() AFTER the API confirms success
		this.putNotifications(unreadIds);
	}

	onNotificationClick(notification: NotificationItem): void {
		if (!notification.is_read) {
			this.putNotifications([notification.id]);
			this.totalNotificationCount = Math.max(0, this.totalNotificationCount - 1);
		}

		switch (notification.content_type) {
			case this.notificationTypeEnum.Course:
				this.navigateToCourse(notification.content_id);
				break;

			case this.notificationTypeEnum.Quiz:
				if (notification.class_id && notification.course_id) {
					this.navigateToQuiz(notification.class_id.toString(), notification.course_id.toString());
				}
				break;

			case this.notificationTypeEnum.Mentor_session:
			case this.notificationTypeEnum.APPOINTMENT_REJECTED:
			case this.notificationTypeEnum.APPOINTMENT_ACCEPTED:
				const tab = this.getMentorSessionTab(notification);
				this.navigateToMentorSession(tab);
				break;

			case this.notificationTypeEnum.SUBSCRIPTION_ACTIVATION:
				this.goToPaymentAndBillingPage();
				break;

			case this.notificationTypeEnum.Job:
				if (notification.content_id && notification.content_id !== '0') {
					this.router.navigate(['/student-dashboard/jobs-detail-page', notification.content_id]);
				} else {
					this.router.navigate(['/student-dashboard/jobs']);
				}
				break;

			case this.notificationTypeEnum.MockAssessment:
				this.router.navigate(['/student-dashboard/placement-prep']);
				break;

			case this.notificationTypeEnum.MockInterview:
				if (notification.content_id && notification.content_id !== '0') {
					const url = notification.content_id;
					if (url.includes('/interview.ai/')) {
						const relativePath = url.substring(url.indexOf('/interview.ai/'));
						window.open(relativePath, '_blank');
					} else {
						window.open(url, '_blank');
					}
				} else {
					window.open('/student-dashboard/ai-interview-dashboard', '_blank');
				}
				break;

			case this.notificationTypeEnum.JobApplication:
			case this.notificationTypeEnum.HiringComplete:
				this.router.navigate(['/student-dashboard/jobs/my-applications']);
				break;

			case this.notificationTypeEnum.Assessment:
			case this.notificationTypeEnum.Interview:
				if (notification.content_id && notification.content_id !== '0') {
					this.router.navigate(['/student-dashboard/jobs/applied', notification.content_id]);
				} else {
					this.router.navigate(['/student-dashboard/jobs/my-applications']);
				}
				break;
		}
	}

	navigateToCourse(content_id: string) {
		this.router.navigate(['/student-dashboard/course-details', content_id]);
	}

	navigateToQuiz(classroomId: string, courseId: string) {
		this.router.navigate(['/student-dashboard/course-detail-page', classroomId, courseId, 'quiz-list']);
	}

	navigateToMentorSession(tab: string) {
		this.router.navigate(['/student-dashboard/my-sessions'], { queryParams: { tab } });
	}

	goToStudentNotifications() {
		this.router.navigate(['student-dashboard/students-notification'])
	}

	goToPaymentAndBillingPage() {
		this.router.navigate(['/student-dashboard/payment-history']);
	}


	toggleDropdown(type: 'notification', event: MouseEvent) {
		event.stopPropagation();
		if (window.innerWidth <= 425 && type === 'notification') {
			this.router.navigate(['/student-dashboard/students-notification']);
			return;
		}
		if (type === 'notification') {
			this.showNotification = !this.showNotification;
			if (this.showNotification) {
				this.isProfileMenuOpen = false;
			}
		}
	}

	@HostListener('document:click', ['$event'])
	handleOutsideClick(event: MouseEvent) {
		if (!this.elementRef.nativeElement.contains(event.target)) {
			this.isProfileMenuOpen = false;
			this.showNotification = false;
			this.isNotificationOpen = false;
		}
	}

	getNotificationIcon(type: string): string {
		switch (type) {
			case 'quiz':
				return 'quiz';
			case 'course':
				return 'school';
			case 'mentor_session':
			case 'appointment_accepted':
			case 'appointment_rejected':
				return 'person';
			case 'subscription_activation':
				return 'payment';
			case this.notificationTypeEnum.Job:
				return 'work';
			default:
				return 'notifications';
		}
	}

	getMentorSessionTab(notification: NotificationItem): string {

		switch (notification.content_type) {

			case this.notificationTypeEnum.APPOINTMENT_ACCEPTED:
				return 'upcoming';

			case this.notificationTypeEnum.APPOINTMENT_REJECTED:
				return 'cancelled';

			case this.notificationTypeEnum.Mentor_session:
				const title = notification.title?.toLowerCase() || '';

				if (title.includes('payment confirmed') || title.includes('payment successful')) {
					return 'confirmed';
				}

				if (title.includes('refund processed') || title.includes('cancelled') || title.includes('rejected')) {
					return 'cancelled';
				}

				if (title.includes('accepted')) {
					return 'upcoming'
				}

				return 'all';
			default:
				return 'all';
		}
	}

	goToSubscription() {
		this.closeProfileMenu();
		this.router.navigate(['/student-dashboard/upgrade-plan']);
	}

	goToPayment() {
		this.closeProfileMenu();
		this.router.navigate(['/student-dashboard/payment-history']);
	}



	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}